import { Injectable, Signal, inject } from '@angular/core';
import { Store } from '@ngrx/store';

import { KpiIds, KpiModel, RawKpi, orderAndFilterKpis } from '@core/models/kpi.model';
import {
  selectKpiDecimals,
  selectKpiHidden,
  selectKpiOrder
} from '@core/store/user-preferences/user-preferences.selectors';

import { SelectItem } from '@shared/components/select/select-item.model';

import {
  KPI_COMPARE_COLORS,
  KpiComparisonItem,
  normalizeRawKpis
} from './kpi-comparison.model';

export interface KpiComparisonSource {
  readonly id: string;
  readonly label: string;
  readonly sublabel?: string;
  readonly rawKpis: RawKpi[] | undefined;
  readonly choices?: SelectItem[];
  readonly choiceId?: string;
}

/**
 * Turns list rows into comparison columns, applying the same KPI preferences the plan panel
 * uses so the two views never disagree on which KPIs exist or in what order.
 *
 * Reads the preferences as signals, so a caller that wraps `buildItems` in a `computed()` gets
 * a panel that re-renders when the user reorders or hides a KPI elsewhere in the app.
 */
@Injectable({ providedIn: 'root' })
export class KpiComparisonService {
  private readonly store = inject(Store);

  readonly order: Signal<KpiIds[]> = this.store.selectSignal(selectKpiOrder);
  private readonly hidden = this.store.selectSignal(selectKpiHidden);
  readonly decimals: Signal<number> = this.store.selectSignal(selectKpiDecimals);

  buildItems(sources: KpiComparisonSource[]): KpiComparisonItem[] {
    return sources.map((source, index) => ({
      id: source.id,
      label: source.label,
      sublabel: source.sublabel,
      color: KPI_COMPARE_COLORS[index % KPI_COMPARE_COLORS.length],
      kpis: this.toKpis(source.rawKpis),
      choices: source.choices,
      choiceId: source.choiceId
    }));
  }

  /** A row's raw `kpis` field as `KpiModel`s, in the user's order and without hidden ones. */
  toKpis(rawKpis: RawKpi[] | undefined): KpiModel[] {
    return orderAndFilterKpis(
      normalizeRawKpis(rawKpis, this.decimals()),
      this.order(),
      this.hidden()
    );
  }
}
